import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { Modal, Form, Input, InputNumber, DatePicker, Select, useApp, Alert, Typography, Button } from '../../ui';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { inr2 } from '../../lib/format';
import { customerOption, CUSTOMER_SELECT_PROPS } from '../../components/CustomerOption';
import { useRetailerSearch } from '../../hooks/useRetailerSearch';

/** The order's GST determination: INTRA_STATE (CGST+SGST) · INTRA_UT (CGST+UTGST) · INTER_STATE (IGST). Null on older orders. */
type TaxNature = string | null | undefined;
type PresetOrder = {
  id: string; orderNo: string; retailerId: string; grandTotal: number | string; orderedQty?: number; pendingQty?: number;
  taxableAmt?: number | string; cgstAmt?: number | string; sgstAmt?: number | string; igstAmt?: number | string; taxNature?: TaxNature;
};
/** Per-shipment (partial) invoicing: bills ONE dispatch. Carries the order context + the shipment id. */
type PresetShipment = { id: string; shipmentNo: string; orderId: string; orderNo: string; retailerId: string; taxNature?: TaxNature };

type Props = {
  open: boolean;
  onClose: () => void;
  /** When opened from an order: locks retailer/order and prefills the total. */
  presetOrder?: PresetOrder | null;
  /** When opened for a specific dispatch (PER_SHIPMENT order): bills that shipment, prefills the target. */
  presetShipment?: PresetShipment | null;
};

type Breakup = { taxableAmt?: number; cgstAmt?: number; sgstAmt?: number; igstAmt?: number };
const num = (v: any) => (v == null || v === '' ? undefined : Number(v));
/** The shipment target's figures, in the form's field names. */
const fromTarget = (t: any): Breakup => (t ? { taxableAmt: num(t.taxable), cgstAmt: num(t.cgst), sgstAmt: num(t.sgst), igstAmt: num(t.igst) } : {});
/** An order's (or order row's) figures, in the form's field names. */
const fromOrder = (o: any): Breakup => (o ? { taxableAmt: num(o.taxableAmt), cgstAmt: num(o.cgstAmt), sgstAmt: num(o.sgstAmt), igstAmt: num(o.igstAmt) } : {});
const cents = (v: any) => Math.round(Number(v ?? 0) * 100);

/**
 * Record a tax invoice produced in Tally: its number/date, taxable value, GST heads and total + the uploaded PDF.
 * The backend posts the retailer ledger and (when an order is linked) marks it INVOICED.
 */
export function InvoiceUploadModal({ open, onClose, presetOrder, presetShipment }: Props) {
  const { message } = useApp();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const canViewRetailers = usePermission('RETAILER.VIEW');
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [pickedOrder, setPickedOrder] = useState<any | null>(null); // standalone: the delivered order linked
  const [pdfUrl, setPdfUrl] = useState<string>('');
  // Set when Save is pressed with no file, so the complaint lands ON the field and not only in a
  // toast that disappears while the empty control stays looking finished.
  const [pdfError, setPdfError] = useState(false);
  // Opens every tax head, for a Tally invoice billed under a different head than the order expects.
  const [allHeads, setAllHeads] = useState(false);

  // Per-shipment: the target totals this dispatch's Tally invoice should match (the guardrail).
  const { data: target } = useQuery({
    queryKey: ['shipment-invoice-target', presetShipment?.id],
    queryFn: () => request<any>({ url: `/shipments/${presetShipment!.id}/invoice-target` }),
    enabled: open && !!presetShipment,
  });

  // Reset the dialog's own state ONCE when the modal opens (or the preset changes). Deliberately excludes
  // `target`: the shipment-invoice-target query resolves after the modal is interactive, and re-running
  // resetFields()/setPdfUrl('') then would wipe a typed invoice number / uploaded PDF. Keyed on the preset
  // IDS, not the objects: the caller builds those inline, so every re-render of its page (a refetch after
  // the accountant comes back from Tally) would otherwise wipe what they had typed.
  useEffect(() => {
    if (open) {
      setPdfUrl('');
      setAllHeads(false);
      setPickedOrder(null);
      setRetailerId(presetShipment?.retailerId ?? presetOrder?.retailerId);
      form.resetFields(); // clears last time's validation errors — the VALUES come from initialValues below
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetOrder?.id, presetShipment?.id, form]);

  /*
   * THE DEFAULTS ARE THE FORM'S initialValues, NOT A setFieldsValue IN THE EFFECT ABOVE.
   *
   * The ui Modal mounts its content only once it is open, and the ui Form seeds its store from
   * initialValues when it mounts — to {} when there are none. The effect ran before that mount, so
   * today's date was written and then wiped: the dialog opened with Invoice date blank and Save was
   * refused with "Required" every time. Grand total survived only because the target query lands after
   * the mount. Read at mount, so the date is the day the dialog is opened; a target already in the
   * query cache seeds straight away, and one that arrives later is still written by the effect below.
   */
  const initialValues = {
    invoiceDate: dayjs(),
    grandTotal: presetShipment
      ? (target?.grand != null ? Number(target.grand) : undefined)
      : presetOrder ? Number(presetOrder.grandTotal) : undefined,
    // Prefilled with what the invoice should carry, so the accountant copies Tally and edits only where it differs.
    ...(presetShipment ? fromTarget(target) : fromOrder(presetOrder)),
    orderId: presetShipment?.orderId ?? presetOrder?.id,
  };

  // When the per-shipment target arrives, seed ONLY its figures (grand total + breakup) — no reset, so
  // nothing the user has typed or uploaded is disturbed.
  useEffect(() => {
    if (open && presetShipment && target?.grand != null) {
      form.setFieldsValue({ grandTotal: Number(target.grand), ...fromTarget(target) });
    }
  }, [open, presetShipment, target, form]);

  // Retailer picker (standalone use only), searched on the server.
  const retailerPick = useRetailerSearch({
    key: 'invoice-upload-retailers',
    enabled: open && !presetOrder && canViewRetailers,
    selected: presetOrder || presetShipment ? undefined : retailerId,
  });

  // DELIVERED orders for the chosen retailer (standalone use), to link the invoice. Every one, not the first 100. CLOSED is
  // left out on purpose: the explicit status already excludes it, and the server refuses to invoice anything but DELIVERED.
  const { data: orders } = useQuery({
    queryKey: ['invoice-upload-orders', retailerId],
    queryFn: () => requestAllPages<any>('/orders', { retailerId, status: 'DELIVERED' }),
    enabled: open && !presetOrder && !!retailerId,
  });

  /*
   * WHICH TAX HEADS TO ASK FOR. Only the ones this order's GST determination uses — CGST + SGST (or
   * UTGST) within a state, IGST across states. An order classified before the GST engine carries no
   * nature, so its own figures decide; with nothing to go on every head is shown. A head the expected
   * figures do use is never hidden, and "billed under another head" opens the rest, because that
   * inverted head is exactly what verification has to be told about.
   */
  const nature: TaxNature = presetShipment ? presetShipment.taxNature : presetOrder ? presetOrder.taxNature : pickedOrder?.gstTaxNature;
  const expected: Breakup = presetShipment ? fromTarget(target) : presetOrder ? fromOrder(presetOrder) : fromOrder(pickedOrder);
  const expIntra = cents(expected.cgstAmt) + cents(expected.sgstAmt) > 0;
  const expInter = cents(expected.igstAmt) > 0;
  const intraDue = nature ? nature !== 'INTER_STATE' : expInter ? false : expIntra ? true : null;
  const showIntra = allHeads || intraDue !== false || expIntra;
  const showInter = allHeads || intraDue !== true || expInter;
  const secondHead = nature === 'INTRA_UT' ? 'UTGST' : 'SGST';

  // Live check: taxable + GST + round-off = grand total, exactly as the Tally invoice prints it.
  const wTaxable = Form.useWatch('taxableAmt', form);
  const wCgst = Form.useWatch('cgstAmt', form);
  const wSgst = Form.useWatch('sgstAmt', form);
  const wIgst = Form.useWatch('igstAmt', form);
  const wGrand = Form.useWatch('grandTotal', form);
  const gstCents = (showIntra ? cents(wCgst) + cents(wSgst) : 0) + (showInter ? cents(wIgst) : 0);
  const roundOffCents = cents(wGrand) - cents(wTaxable) - gstCents;
  const canCheck = wTaxable != null && wGrand != null;
  // Tally rounds the grand total to the rupee, so anything under ₹1 between the parts and the total is its round-off.
  const addsUp = !canCheck || Math.abs(roundOffCents) < 100;

  const save = useConfirmedMutation({ title: "Attach this invoice?", content: "It becomes the invoice of record for this order.", okText: "Yes, attach" }, {
    mutationFn: (vals: any) =>
      request({
        url: '/invoices',
        method: 'POST',
        data: {
          invoiceNo: String(vals.invoiceNo).trim(),
          invoiceDate: vals.invoiceDate.format('YYYY-MM-DD'),
          dueDate: vals.dueDate ? vals.dueDate.format('YYYY-MM-DD') : undefined,
          grandTotal: Number(vals.grandTotal),
          taxableAmt: Number(vals.taxableAmt),
          cgstAmt: Number(vals.cgstAmt ?? 0),
          sgstAmt: Number(vals.sgstAmt ?? 0),
          igstAmt: Number(vals.igstAmt ?? 0),
          pdfUrl,
          orderId: presetShipment?.orderId ?? presetOrder?.id ?? vals.orderId ?? undefined,
          shipmentId: presetShipment?.id ?? undefined,
          retailerId: (presetOrder || presetShipment) ? undefined : retailerId,
        },
      }),
    onSuccess: () => {
      message.success('Tally invoice recorded');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['outstanding-aging'] });
      const oid = presetShipment?.orderId ?? presetOrder?.id;
      if (oid) {
        qc.invalidateQueries({ queryKey: ['order', oid] });
        qc.invalidateQueries({ queryKey: ['order-invoices', oid] });
        qc.invalidateQueries({ queryKey: ['order-timeline', oid] });
        qc.invalidateQueries({ queryKey: ['order-shipments', oid] });
      }
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to record invoice'),
  });

  const submit = () => {
    if (!pdfUrl) { setPdfError(true); message.error('Upload the Tally invoice PDF'); return; }
    setPdfError(false);
    if (!presetOrder && !presetShipment && !retailerId) { message.error('Select a retailer'); return; }
    form.submit();
  };
  const finish = (v: any) => {
    // The mismatch is already printed under the grand total; refuse here too so it cannot be saved past.
    if (!addsUp) { message.error('The taxable value and GST do not add up to the grand total'); return; }
    // A head that is not shown was not asked for — send it as nil rather than a stale prefill.
    save.mutate({ ...v, cgstAmt: showIntra ? v.cgstAmt : 0, sgstAmt: showIntra ? v.sgstAmt : 0, igstAmt: showInter ? v.igstAmt : 0 });
  };
  const inr = inr2;
  // Whole-order invoice on a partially-packed order → warn: only part is packed, don't over-bill.
  const packedQty = presetOrder ? Math.max(0, Number(presetOrder.orderedQty ?? 0) - Number(presetOrder.pendingQty ?? 0)) : 0;
  const partialPacked = !!presetOrder && Number(presetOrder.pendingQty ?? 0) > 0.001 && packedQty > 0;

  const headCount = 1 + (showIntra ? 2 : 0) + (showInter ? 1 : 0);
  const moneyInput = <InputNumber style={{ width: '100%' }} min={0} step={1} precision={2} />;
  const sign = (c: number) => (c < 0 ? '−' : '+');
  const sumLine = !canCheck ? null : addsUp ? (
    <span>
      Taxable {inr(Number(wTaxable))} + GST {inr(gstCents / 100)}
      {roundOffCents !== 0 ? <> {sign(roundOffCents)} round-off {inr(Math.abs(roundOffCents) / 100)}</> : null}
      {' '}= {inr(Number(wGrand))}
    </span>
  ) : (
    <span className="text-danger-500" role="alert">
      Taxable {inr(Number(wTaxable))} + GST {inr(gstCents / 100)} comes to {inr((cents(wTaxable) + gstCents) / 100)},
      {' '}{inr(Math.abs(roundOffCents) / 100)} {roundOffCents > 0 ? 'short of' : 'more than'} the grand total. Only a round-off
      of under ₹1 can sit between them — check these figures against the Tally invoice.
    </span>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose() }}
      title={presetShipment ? `Upload Tally invoice — shipment ${presetShipment.shipmentNo}` : presetOrder ? `Upload Tally invoice — order ${presetOrder.orderNo}` : 'Upload Tally invoice'}
      width={620}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={submit}>Save invoice</Button>
      </>}
    >
      {presetShipment ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Invoice ONLY this shipment — not the whole order."
          description={target
            ? (() => {
              // The parts must add up to the target: "taxable ₹20,203.20 + GST ₹2,916.22" came to ₹23,119.42 against a
              // ₹23,119 target, because the round-off was left out (E2E 15 Sep).
              const gst = Number(target.cgst) + Number(target.sgst) + Number(target.igst);
              const roundOff = Math.round((Number(target.grand) - Number(target.taxable) - gst) * 100) / 100;
              return <span>Bill the packed quantity in this shipment. <b>Target: {inr(target.grand)}</b> (taxable {inr(target.taxable)} + GST {inr(gst)}{Math.abs(roundOff) >= 0.01 ? <> {roundOff < 0 ? '−' : '+'} round-off {inr(Math.abs(roundOff))}</> : null}). A different total will be flagged as a mismatch and block dispatch.</span>;
            })()
            : 'Bill only the packed quantity in this shipment; the whole order is invoiced across its shipments.'}
        />
      ) : (
        <>
          {partialPacked && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Only ${packedQty} of ${presetOrder!.orderedQty} units are packed — ${presetOrder!.pendingQty} still pending.`}
              description="If you're dispatching only what's packed, bill just those units in Tally. The figures below default to the whole order — adjust them to match what you're actually invoicing. (New orders use per-shipment invoicing, which bills each dispatch automatically.)"
            />
          )}
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Record the tax invoice raised in Tally."
            description="Enter the Tally invoice number and date, its taxable value, GST and grand total as Tally printed them, and attach the PDF. This posts the retailer's outstanding; for an order awaiting dispatch, it unlocks dispatch once recorded."
          />
        </>
      )}
      <Form
        key={presetShipment ? `shipment-${presetShipment.id}` : presetOrder ? `order-${presetOrder.id}` : 'standalone'}
        form={form} layout="vertical" initialValues={initialValues} onFinish={finish}
      >
        {presetShipment ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Linked to shipment <b>{presetShipment.shipmentNo}</b> of order <b>{presetShipment.orderNo}</b>.
          </Typography.Paragraph>
        ) : presetOrder ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Linked to order <b>{presetOrder.orderNo}</b>.
          </Typography.Paragraph>
        ) : (
          <>
            <Form.Item label="Retailer" required>
              <Select
                placeholder="Select retailer"
                {...CUSTOMER_SELECT_PROPS}
                filterOption={false} onSearch={retailerPick.onSearch}
                value={retailerId}
                onChange={(v) => { setRetailerId(v); setPickedOrder(null); form.setFieldValue('orderId', undefined); }}
                options={retailerPick.rows.map((r: any) => customerOption(r))}
              />
            </Form.Item>
            <Form.Item name="orderId" label="Link to delivered order (optional)">
              <Select
                placeholder={retailerId ? 'Select a delivered order' : 'Pick a retailer first'}
                allowClear
                disabled={!retailerId}
                onChange={(_v) => {
                  const o = (orders?.data ?? []).find((x: any) => String(x.id) === String(_v));
                  setPickedOrder(o ?? null);
                  if (o) form.setFieldsValue({ grandTotal: Number(o.grandTotal), ...fromOrder(o) });
                }}
                options={(orders?.data ?? []).map((o: any) => ({
                  value: String(o.id),
                  label: `${o.orderNo} · ${inr2(o.grandTotal)}`,
                }))}
              />
            </Form.Item>
          </>
        )}

        <Form.Item name="invoiceNo" label="Tally invoice number" rules={[{ required: true, message: 'Required' }]}>
          <Input placeholder="e.g. GB/2526/001234" />
        </Form.Item>
        <Form.Item name="invoiceDate" label="Invoice date" rules={[{ required: true, message: 'Required' }]}>
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d.isAfter(dayjs().endOf('day'))} />
        </Form.Item>
        <Form.Item name="dueDate" label="Due date (optional — defaults to credit period)">
          <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
        </Form.Item>
        <div className={headCount === 3 ? 'grid grid-cols-1 gap-x-3 sm:grid-cols-3' : 'grid grid-cols-1 gap-x-3 sm:grid-cols-2'}>
          <Form.Item name="taxableAmt" label="Taxable value (₹)" rules={[{ required: true, message: 'Required' }]}>
            {moneyInput}
          </Form.Item>
          {showIntra && (
            <>
              <Form.Item name="cgstAmt" label="CGST (₹)" rules={[{ required: true, message: 'Required' }]}>
                {moneyInput}
              </Form.Item>
              <Form.Item name="sgstAmt" label={`${secondHead} (₹)`} rules={[{ required: true, message: 'Required' }]}>
                {moneyInput}
              </Form.Item>
            </>
          )}
          {showInter && (
            <Form.Item name="igstAmt" label="IGST (₹)" rules={[{ required: true, message: 'Required' }]}>
              {moneyInput}
            </Form.Item>
          )}
        </div>
        {!(showIntra && showInter) && (
          <div className="-mt-2 mb-3">
            <Button variant="text" size="sm" onClick={() => setAllHeads(true)}>
              {showIntra ? 'Tally charged IGST instead?' : `Tally charged CGST + ${secondHead} instead?`}
            </Button>
          </div>
        )}
        <Form.Item name="grandTotal" label="Grand total (₹)" rules={[{ required: true, message: 'Required' }]} extra={sumLine}>
          <InputNumber style={{ width: '100%' }} min={1} step={1} precision={2} />
        </Form.Item>
        <Form.Item
          label="Tally invoice PDF"
          required
          help={pdfError ? 'The Tally invoice PDF is required — the invoice cannot be recorded without it.' : undefined}
          validateStatus={pdfError ? 'error' : undefined}
        >
          <FileUploadField
            category="invoice" buttonLabel="Upload invoice PDF" value={pdfUrl}
            onChange={(v) => { setPdfUrl(v); if (v) setPdfError(false); }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
